import { AccessDeniedCodeSchema } from '@gestion-permisos/contracts';
import type { ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { loginUrl } from '../api/login.js';
import { buttonClasses } from '../components/button-styles.js';

/** Pantallas públicas: no llaman a `/me` ni a ningún otro endpoint. */
function PublicPage({ title, children }: { title: string; children: ReactNode }) {
  return (
    // Fuera del marco: el fondo y el color de la aplicación cubren toda la ventana, como en `AppShell`.
    <main className="min-h-screen bg-background text-foreground">
      <div className="mx-auto max-w-xl p-6">
        <h1 className="text-2xl font-semibold">{title}</h1>
        <div className="mt-3 space-y-3">{children}</div>
      </div>
    </main>
  );
}

/** El enlace al inicio de sesión es una navegación de página completa hacia el BFF (no del enrutador). */
function LoginLink({ returnTo }: { returnTo?: string | null }) {
  return (
    <a href={loginUrl(returnTo)} className={buttonClasses()}>
      Iniciar sesión
    </a>
  );
}

export function SessionClosedPage() {
  return (
    <PublicPage title="Sesión cerrada">
      <p>Has cerrado la sesión.</p>
      <LoginLink />
    </PublicPage>
  );
}

/** `?volver=` lleva la ruta en la que expiró la sesión; solo se usa si es una ruta relativa válida. */
export function SessionExpiredPage() {
  const [params] = useSearchParams();
  return (
    <PublicPage title="Sesión expirada">
      <p>Tu sesión ha caducado. Vuelve a iniciar sesión para continuar.</p>
      <LoginLink returnTo={params.get('volver')} />
    </PublicPage>
  );
}

const ACCESS_DENIED_MESSAGES = {
  NO_ACTIVE_EMPLOYEE:
    'Tu cuenta no corresponde a un empleado activo de la aplicación. Si crees que es un error, contacta con un administrador.',
} as const;

/**
 * Acceso denegado (H-04). Muestra solo un código conocido; un valor desconocido no se repite en pantalla.
 * La URL nunca lleva datos personales: los fija la API.
 */
export function AccessDeniedPage() {
  const [params] = useSearchParams();
  const code = AccessDeniedCodeSchema.safeParse(params.get('code'));
  return (
    <PublicPage title="Acceso denegado">
      <p>{code.success ? ACCESS_DENIED_MESSAGES[code.data] : 'No tienes acceso a la aplicación.'}</p>
      {code.success ? <p className="text-sm text-muted-foreground">Código: {code.data}</p> : null}
    </PublicPage>
  );
}

export function NotFoundPage() {
  return (
    <PublicPage title="Página no encontrada">
      <p>La dirección no corresponde a ninguna pantalla.</p>
      <Link to="/" className="text-primary underline underline-offset-4">
        Ir al inicio
      </Link>
    </PublicPage>
  );
}
